import Emittery from 'emittery';
import { useEffect, useReducer } from 'react';

type IconComponent = React.ComponentType<{ className?: string }>;

export const normalizeAppKey = (name: string): string =>
  name
    .toLowerCase()
    .replace(/\.app$/, '')
    .replace(/[^\p{L}\p{N}]/gu, '');

const SYSTEM_APP_BLOCKLIST = new Set([
  'coreautha',
  'securityagent',
  'usernotificationcenter',
  'universalaccessauthwarn',
  'notchedup',
  'monitorcontrol',
  'simulator',
  'qemusystemaarch64',
  'loginwindow',
  'windowserver',
  'controlcenter',
  'dock',
  'finder',
]);

export function isSystemApp(app: string): boolean {
  return SYSTEM_APP_BLOCKLIST.has(normalizeAppKey(app));
}

const MONOGRAM_CACHE = new Map<string, IconComponent>();

const buildMonogram = (letter: string): IconComponent => {
  const cached = MONOGRAM_CACHE.get(letter);
  if (cached) return cached;
  const Monogram = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 24 24" className={className}>
      <circle
        cx="12"
        cy="12"
        r="12"
        className="[fill:var(--secondary-default)]"
      />
      <text
        x="12"
        y="12"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="13"
        fontWeight="500"
        className="[fill:var(--foreground-muted)]"
      >
        {letter}
      </text>
    </svg>
  );
  Monogram.displayName = `CapturedAppMonogram(${letter})`;
  MONOGRAM_CACHE.set(letter, Monogram);
  return Monogram;
};

export interface CapturedAppIcon {
  Icon: IconComponent;
  displayName: string;
}

export function getCapturedAppIcon(rawAppName: string): CapturedAppIcon {
  const displayName = rawAppName.trim();
  const letter = displayName.charAt(0).toUpperCase() || '?';
  return { Icon: buildMonogram(letter), displayName };
}

type IconState = string | null | 'pending';
const RUNTIME_CACHE_MAX = 50;
const RUNTIME_CACHE = new Map<string, IconState>();

const runtimeIconChangeEvent = 'change';
const runtimeIconEmitter = new Emittery<{
  [runtimeIconChangeEvent]: undefined;
}>();

function rememberRuntimeIcon(key: string, state: IconState): void {
  if (RUNTIME_CACHE.has(key)) {
    RUNTIME_CACHE.delete(key);
  } else if (RUNTIME_CACHE.size >= RUNTIME_CACHE_MAX) {
    const oldest = RUNTIME_CACHE.keys().next().value;
    if (oldest !== undefined) RUNTIME_CACHE.delete(oldest);
  }
  RUNTIME_CACHE.set(key, state);
}

function notifySubscribers(): void {
  runtimeIconEmitter.emit(runtimeIconChangeEvent);
}

function requestRuntimeIcon(displayName: string, key: string): void {
  if (RUNTIME_CACHE.get(key) !== undefined) return;
  rememberRuntimeIcon(key, 'pending');
  const api = typeof window !== 'undefined' ? window.mainProcessApi : undefined;
  if (!api?.getAppIconByName) {
    rememberRuntimeIcon(key, null);
    return;
  }
  api
    .getAppIconByName(displayName)
    .then((dataUrl) => {
      rememberRuntimeIcon(key, dataUrl ?? null);
      notifySubscribers();
    })
    .catch(() => {
      rememberRuntimeIcon(key, null);
      notifySubscribers();
    });
}

export type IconResolutionState = 'pending' | 'resolved' | 'missing';

export function useCapturedAppRuntimeIcons(
  apps: string[]
): Map<string, { state: IconResolutionState; dataUrl: string | null }> {
  const [, force] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    const unsub = runtimeIconEmitter.on(runtimeIconChangeEvent, force);
    return unsub;
  }, []);

  useEffect(() => {
    for (const app of apps) {
      const displayName = app.trim();
      requestRuntimeIcon(displayName, normalizeAppKey(displayName));
    }
  }, [apps]);

  const result = new Map<
    string,
    { state: IconResolutionState; dataUrl: string | null }
  >();
  for (const app of apps) {
    const displayName = app.trim();
    const key = normalizeAppKey(displayName);
    const cached = RUNTIME_CACHE.get(key);
    if (cached === undefined || cached === 'pending') {
      result.set(app, { state: 'pending', dataUrl: null });
    } else if (cached === null) {
      result.set(app, { state: 'missing', dataUrl: null });
    } else {
      result.set(app, { state: 'resolved', dataUrl: cached });
    }
  }
  return result;
}
